import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';
import { useFavorites } from '@/hooks/useFavorites';
import { getRelatedWorks, getWorkById, typeLabel } from '@/utils/works';
import RelatedWorks from './components/RelatedWorks';

const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

export default function DetailPage() {
  const { id = '' } = useParams();
  const work = getWorkById(id);
  const related = work ? getRelatedWorks(work) : [];
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSEO({
    title: work
      ? `${work.title}剧情介绍与演员信息 - 年轻的岳母`
      : '影视详情 - 年轻的岳母',
    description: work
      ? `${work.title}（${work.year}，${work.region}）：${work.synopsis.slice(0, 70)}…`
      : '年轻的岳母影视详情页，提供剧情介绍与演员信息。',
    keywords: work
      ? `${work.title},${typeLabel(work.type)},剧情介绍,演员信息,年轻的岳母`
      : '年轻的岳母,影视详情',
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!work) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-emotion-sad-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该影视内容</h1>
          <p className="mt-2 text-sm text-foreground-500">
            该作品可能已下架或链接有误，看看其他精彩内容吧
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link
              to="/"
              className="px-5 h-10 inline-flex items-center rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/category/movie"
              className="px-5 h-10 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览电影
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const favorited = isFavorite(work.id);
  const metaItems = [
    { label: '类型', value: work.genres.join(' / ') },
    { label: '地区', value: work.region },
    { label: '上映年份', value: String(work.year) },
    { label: '导演', value: work.director },
    { label: '主演', value: work.actors.join('、') },
    { label: '集数', value: work.episodes > 0 ? `全 ${work.episodes} 集` : '电影' },
  ];

  return (
    <Layout>
      {/* Banner */}
      <section className="relative">
        <div className="absolute inset-0 h-72 md:h-80 overflow-hidden">
          <img
            src={work.backdrop}
            alt={`${work.title} 剧照`}
            title={`${work.title} 影视剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/80 to-secondary-950/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-8">
          <Breadcrumb
            tone="inverted"
            items={[
              { label: '首页', to: '/' },
              { label: typeLabel(work.type), to: `/category/${work.type}` },
              { label: work.title },
            ]}
          />

          <div className="mt-6 flex flex-col sm:flex-row gap-6">
            <div className="w-40 sm:w-48 shrink-0 mx-auto sm:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border-2 border-background-50/20">
                <img
                  src={work.cover}
                  alt={`${work.title} 海报`}
                  title={`${work.title} 主封面`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 pb-2">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-secondary-950">
                  {typeLabel(work.type)}
                </span>
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 border border-background-50/20 backdrop-blur">
                  {work.region}
                </span>
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 border border-background-50/20 backdrop-blur">
                  {work.year}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">
                {work.title}
              </h1>

              <div className="mt-3 flex items-center gap-3">
                <span className="flex items-center gap-1 text-accent-400 font-bold text-lg">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {work.rating.toFixed(1)}
                </span>
                <span className="text-sm text-background-300">/ 10 分</span>
                <span className="text-sm text-background-300 flex items-center gap-1">
                  <i className="ri-fire-line text-accent-400" aria-hidden="true"></i>
                  热度 {work.hot}
                </span>
              </div>

              <p className="mt-3 text-sm text-background-200">
                主演：{work.actors.join('、')} · 导演：{work.director}
              </p>
              <p className="mt-1 text-sm text-background-300">最近更新：{work.updateTime}</p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(work.id)}
                  className={`inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-secondary-950 hover:bg-accent-600'
                      : 'bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} aria-hidden="true"></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyText(window.location.href);
                    showToast(ok ? '链接已复制到剪贴板' : '复制失败，请手动复制地址栏链接');
                  }}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-medium bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-line" aria-hidden="true"></i>
                  分享
                </button>
                <Link
                  to={`/category/${work.type}`}
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-medium text-background-200 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-right-circle-line" aria-hidden="true"></i>
                  更多{typeLabel(work.type)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <article className="lg:col-span-2">
            <section className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-600" aria-hidden="true"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                {work.synopsis}
              </p>
            </section>

            <section className="mt-6 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-information-line text-primary-600" aria-hidden="true"></i>
                详细信息
              </h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {metaItems.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm py-1.5 border-b border-background-100">
                    <dt className="w-20 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="flex-1 text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="p-5 rounded-lg bg-background-50 border border-background-200 lg:sticky lg:top-20">
              <h3 className="font-heading text-base font-bold text-foreground-950">主演阵容</h3>
              <ul className="mt-3 space-y-2.5">
                {work.actors.map((actor) => (
                  <li key={actor} className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center text-sm font-medium">
                      {actor.slice(0, 1)}
                    </span>
                    <span className="text-sm text-foreground-800">{actor}</span>
                  </li>
                ))}
                <li className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className="ri-clapperboard-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-foreground-800">导演 · {work.director}</span>
                </li>
              </ul>

              <div className="mt-5 pt-5 border-t border-background-200">
                <p className="text-xs text-foreground-500 mb-2">所属分类</p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`/category/${work.type}`}
                    className="px-3 py-1.5 rounded-full text-xs bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors cursor-pointer"
                  >
                    {typeLabel(work.type)}
                  </Link>
                  {work.genres.map((g) => (
                    <span
                      key={g}
                      className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-800"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>

              <p className="mt-5 text-[11px] leading-relaxed text-foreground-400">
                本站仅提供影视资料展示，不含在线播放与下载功能。收藏为本地演示功能，刷新后重置。
              </p>
            </div>
          </aside>
        </div>

        <RelatedWorks works={related} currentId={work.id} />
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-secondary-950 text-background-50 text-sm shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </Layout>
  );
}